import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
// Lucide's stroke icons refuse to paint inside this one button in testing
// (fine everywhere else on the page) - the prod-proven FontAwesome pair
// renders reliably here, so the SSN toggle keeps it.
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import {
  Lock,
  ShieldCheck,
  CheckCircle2,
  Smartphone,
  Loader2,
  UserPlus,
  UserRound,
  TriangleAlert,
} from 'lucide-react';

import PublicLayout from '../layouts/PublicLayout';
import AdminSelect from '../common/AdminSelect';
import { useAuthRegister, useSendPhoneVerificationCode, useVerifyPhoneCode } from '../../services/auth';
import { useFetchAuthInvitation } from '../../services/invitations';
import { getVaOutagePublic } from '../../services/vaOutage';
import { useUser } from '../../contexts/UserContext';
// Fixed dark-grey brand theme shared with /referral + /partner-signup
// (hardcoded per branding, not the app's light/dark tokens).
import { BUTTON, INPUT, LABEL, PAGE_BG, SELECT_DARK, useBrandBodyBackground } from '../referral/brandTheme';

const HEADING = 'text-base font-semibold text-white';

// Per-toast theming for this page only: the default white toasts look broken
// against the dark brand theme.
const TOAST = { theme: 'dark', className: '!rounded-xl' };

// bind: put the id on the child (the input). Off when the child is a wrapper
// whose own input already carries the id (QA 2026-10-13: two elements had it).
const Field = ({ id, label, required, children, className, bind = true }) => (
  <div className={className}>
    <label htmlFor={id} className={LABEL}>
      {label}
      {required ? <span className="text-rose-400"> *</span> : null}
    </label>
    {bind ? React.cloneElement(children, { id }) : children}
  </div>
);

const RegisterV2 = () => {
  const { signIn } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = React.useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    ssn: '',
    agreeToContact: false,
    userReferredBy: null,
    userPartnerId: null,
    adminSelect: null,
    inviteId: null,
  });

  const [touched, setTouched] = React.useState({ ssn: false, phone: false, dob: false });
  const [showSsn, setShowSsn] = React.useState(false);
  const [isVaOutage, setIsVaOutage] = React.useState(false);
  const [inviteId, setInviteId] = React.useState(null);
  const [partner, setPartner] = React.useState(null);

  // Phone verification state
  const [phoneVerified, setPhoneVerified] = React.useState(false);
  const [verificationCodeSent, setVerificationCodeSent] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [cooldownSeconds, setCooldownSeconds] = React.useState(0);
  const cooldownIntervalRef = React.useRef(null);

  const { isLoading: inviteLoading, data: invite } = useFetchAuthInvitation(inviteId);

  useBrandBodyBackground();

  const stripNonDigits = (val) => (val || '').replace(/\D/g, '');

  const validateSSN = (val) => {
    const digits = stripNonDigits(val);
    if (digits.length !== 9) return 'Please enter a valid 9-digit SSN.';
    const area = digits.slice(0, 3);
    const group = digits.slice(3, 5);
    const serial = digits.slice(5);
    if (area === '000' || area === '666' || Number(area) >= 900) return 'Please enter a valid SSN.';
    if (group === '00' || serial === '0000') return 'Please enter a valid SSN.';
    const blacklist = new Set([
      '000000000',
      '111111111',
      '222222222',
      '333333333',
      '444444444',
      '555555555',
      '666666666',
      '777777777',
      '888888888',
      '999999999',
      '123456789',
      '987654321',
      '078051120', // most infamous fake SSN
    ]);
    if (blacklist.has(digits)) return 'Please enter a valid SSN.';
    return '';
  };

  // At least 17, the youngest anyone can enlist (Nic 2026-10-21). Same rule
  // as the server (registerController oldEnoughToSignUp).
  // Today in Central, 17 years back; a Feb 29 with no match that year is
  // Feb 28 (the server's latestSignupDob, exactly).
  const latestDob = (() => {
    const p = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' })
        .formatToParts(new Date())
        .map((x) => [x.type, x.value]),
    );
    const y = Number(p.year) - 17;
    const m = Number(p.month);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const d = Math.min(Number(p.day), last);
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  })();
  // Typed, not a calendar (Nic 2026-10-26): the browser's date picker was
  // laggy and easy to land on the wrong year. They type MM/DD/YYYY; the
  // account gets YYYY-MM-DD.
  const dobIsoOf = (val) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(val || '');
    if (!m) return null;
    const [, mm, dd, yyyy] = m;
    const d = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
    if (d.getUTCFullYear() !== Number(yyyy) || d.getUTCMonth() !== Number(mm) - 1 || d.getUTCDate() !== Number(dd)) {
      return null;
    }
    return `${yyyy}-${mm}-${dd}`;
  };
  const validateDob = (val) => {
    if (!val) return '';
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return 'Enter it as MM/DD/YYYY.';
    const iso = dobIsoOf(val);
    if (!iso) return "That date doesn't exist. Check the month and day.";
    if (iso < '1900-01-01' || iso > latestDob) return 'Check the year: you need to be at least 17.';
    return '';
  };
  const dobIso = dobIsoOf(form.dateOfBirth);
  const dobReadBack = dobIso
    ? new Date(`${dobIso}T12:00:00Z`).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : null;

  const validatePhone = (val) => {
    const digits = stripNonDigits(val);
    if (digits.length !== 10) return 'Please enter a valid 10-digit phone number.';
    const area = digits.slice(0, 3);
    const blacklist = new Set(['0000000000', '1111111111', '1234567890', '2222222222', '5555555555', '9999999999']);
    if (blacklist.has(digits)) return 'Please enter a valid phone number.';
    if (area === '000' || area === '555' || area.startsWith('0') || area.startsWith('1')) return 'Please enter a valid phone number.';
    return '';
  };

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Reset phone verification if phone number changes
    if (name === 'phone') {
      setPhoneVerified(false);
      setVerificationCodeSent(false);
      setVerificationCode('');
    }
  };

  const ssnError = validateSSN(form.ssn);
  const dobError = validateDob(form.dateOfBirth);
  const showDobError = touched.dob && !!dobError;
  const phoneError = validatePhone(form.phone);
  const showSsnError = touched.ssn && !!ssnError;
  const showPhoneError = touched.phone && !!phoneError;
  const isPhoneValid = !validatePhone(form.phone);

  // Start cooldown countdown
  const startCooldown = React.useCallback((seconds) => {
    setCooldownSeconds(seconds);
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
    }
    cooldownIntervalRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownIntervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Cleanup interval on unmount
  React.useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  // Send verification code mutation
  const sendCodeMutation = useSendPhoneVerificationCode(
    (res) => {
      setVerificationCodeSent(true);
      startCooldown(res.cooldownSeconds || 60);
      toast.success('Verification code sent to your phone!', TOAST);
    },
    {
      onError: (err) => {
        const msg = err.response?.data?.msg || 'Failed to send verification code.';
        toast.error(msg, TOAST);
      },
    },
  );

  // Verify code mutation
  const verifyCodeMutation = useVerifyPhoneCode(
    (res) => {
      if (res.verified) {
        setPhoneVerified(true);
        setVerificationCodeSent(false);
        toast.success('Phone number verified!', TOAST);
      }
    },
    {
      onError: (err) => {
        const msg = err.response?.data?.msg || 'Invalid verification code.';
        toast.error(msg, TOAST);
      },
    },
  );

  const handleSendCode = () => {
    if (!isPhoneValid) {
      setTouched((prev) => ({ ...prev, phone: true }));
      return;
    }
    sendCodeMutation.mutate(form.phone);
  };

  // Format verification code as 000-000
  const formatVerificationCode = (val) => {
    const digits = (val || '').replace(/\D/g, '').slice(0, 6);
    if (digits.length <= 3) return digits;
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  };

  const getVerificationCodeDigits = () => verificationCode.replace(/\D/g, '');

  const handleVerifyCode = () => {
    const digits = getVerificationCodeDigits();
    if (digits.length !== 6) {
      toast.error('Please enter the 6-digit code.', TOAST);
      return;
    }
    verifyCodeMutation.mutate({ phone: form.phone, code: digits });
  };

  const callApi = useAuthRegister(
    (res) => {
      if (res && res._token) {
        // Auto-login then navigate to processing
        signIn(res, () => navigate('/processing'));
        return;
      }
      // Fallback to sign-in
      navigate('/sign-in');
    },
    {
      onError: (err) => toast.error(err.message, TOAST),
    },
  );

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await getVaOutagePublic();
        setIsVaOutage(!!(data && data.isOutage));
      } catch (_) {}
    };
    load();
  }, []);

  // Handle invite ID from URL
  React.useEffect(() => {
    const id = new URLSearchParams(location.search).get('inviteId');
    setInviteId(id || null);
  }, [location]);

  // Handle referral from location state
  React.useEffect(() => {
    if (location.state && location.state.userReferredBy) {
      setForm((prev) => ({
        ...prev,
        adminSelect: location.state.userReferredBy,
        userReferredBy: location.state.userReferredBy.value,
      }));
    }

    if (location.state && location.state.partner) {
      setForm((prev) => ({
        ...prev,
        userPartnerId: location.state.partner.partnerId,
      }));
      setPartner(location.state.partner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Populate form from invitation data
  React.useEffect(() => {
    if (!inviteId || !invite) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      inviteId: invite.inviteId,
      userReferredBy: invite.inviteByUserId,
      email: invite.inviteEmail,
      // Explicit mapping: the invite meta's keys (userFirstName/...) don't
      // match the form's field names, so a bare spread prefilled nothing.
      firstName: invite.inviteMeta?.userFirstName || prev.firstName,
      lastName: invite.inviteMeta?.userLastName || prev.lastName,
    }));
  }, [invite, inviteId]);

  const onSubmit = (e) => {
    e.preventDefault();

    // mark as touched to reveal inline validation
    setTouched((prev) => ({ ...prev, ssn: true, phone: true, dob: true }));

    if (validateSSN(form.ssn) || validatePhone(form.phone) || validateDob(form.dateOfBirth)) {
      return;
    }

    if (!phoneVerified) {
      toast.error('Please verify your phone number before submitting.', TOAST);
      return;
    }

    // Address is intentionally NOT collected during signup. Veritas fills it
    // in from EditClaimModal after onboarding; the In Process status gate
    // enforces that the address is on file (and Smarty-verified) before any
    // claim can advance through the workflow.
    const payload = {
      userFirstName: (form.firstName || '').trim(),
      userLastName: (form.lastName || '').trim(),
      userEmail: (form.email || '').trim().toLowerCase(),
      userDateOfBirth: dobIso,
      userPhone: stripNonDigits(form.phone),
      userSsn: stripNonDigits(form.ssn),
      userType: 'pending_client',
      userReferredBy: form.userReferredBy,
      userPartnerId: form.userPartnerId,
      inviteId: form.inviteId,
    };

    callApi.mutate(payload);
  };

  // Contact consent is REQUIRED: the whole onboarding flow calls, texts,
  // and emails this person (confirm call, signing link).
  const canSubmit = phoneVerified && form.agreeToContact && !callApi.isLoading;

  return (
    <PublicLayout
      background={PAGE_BG}
      footerClassName="text-neutral-300"
      footerVariant="dark"
    >
      <div className="mx-auto max-w-xl py-6">
        <div className="rounded-2xl bg-[#4A4A4E] p-6 shadow-lg sm:p-8">
          <div className="mb-5 text-center">
            <div className="mx-auto mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10">
              <UserPlus className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">Start Your Claim</h2>
            <p className="mb-0 text-center text-sm text-neutral-300">
              Ready to start a new claim with us? Complete the form below to
              get started.
            </p>
          </div>

          {isVaOutage ? (
            <div className="mb-4 flex items-start gap-3 rounded-xl bg-amber-400/10 p-4">
              <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <p className="mb-0 text-sm text-amber-100">
                <span className="font-semibold">VA outage notice:</span>{' '}
                onboarding may take longer than usual. Expect a call from our
                team to complete your registration.
              </p>
            </div>
          ) : null}

          {inviteLoading ? (
            <p className="mb-4 text-center text-sm text-neutral-300">
              Checking invite details...
            </p>
          ) : null}

          {partner ? (
            <div className="mb-4 rounded-xl bg-emerald-400/10 p-4">
              <p className={`mb-0 ${HEADING}`}>
                Welcome! So glad {partner.partnerName} sent you our way!
              </p>
              {partner?.partnerDetails ? (
                <p className="mb-0 mt-1 text-sm text-neutral-300">
                  {partner.partnerDetails.split('\n').map((item, key) => (
                    <span key={key}>
                      {item}
                      <br />
                    </span>
                  ))}
                </p>
              ) : null}
            </div>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <h3 className={`mb-4 flex items-center gap-2 ${HEADING}`}>
                <UserRound className="h-4 w-4" />
                About you
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field id="register-firstName" label="First name" required>
                  <input
                    className={INPUT}
                    name="firstName"
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={onChange}
                    required
                  />
                </Field>
                <Field id="register-lastName" label="Last name" required>
                  <input
                    className={INPUT}
                    name="lastName"
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={onChange}
                    required
                  />
                </Field>
                <Field id="register-dob" label="Date of birth" required bind={false}>
                  <InputMask
                    id="register-dob"
                    className={INPUT}
                    name="dateOfBirth"
                    mask="99/99/9999"
                    maskChar={null}
                    placeholder="MM/DD/YYYY"
                    inputMode="numeric"
                    autoComplete="bday"
                    value={form.dateOfBirth}
                    onChange={onChange}
                    // At least 17 (Nic 2026-10-21), and not before any
                    // plausible living veteran: checked in validateDob.
                    onBlur={() => setTouched((p) => ({ ...p, dob: true }))}
                    required
                  />
                  {showDobError ? (
                    <p className="mb-0 mt-1 text-xs text-rose-300">{dobError}</p>
                  ) : dobReadBack && !dobError ? (
                    <p className="mb-0 mt-1 text-xs text-slate-400">{dobReadBack}</p>
                  ) : null}
                </Field>
                <Field id="register-email" label="Email" required>
                  <input
                    className={INPUT}
                    type="email"
                    inputMode="email"
                    name="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="your@email.com"
                    required
                  />
                </Field>
              </div>

              {!form.inviteId && !partner ? (
                <div className="mt-3">
                  <label className={LABEL}>Who were you referred by?</label>
                  <AdminSelect
                    usePublic={true}
                    value={form.adminSelect}
                    onChange={(val) => {
                      setForm((prev) => ({
                        ...prev,
                        adminSelect: val,
                        userReferredBy: val ? val.value : null,
                      }));
                    }}
                    showDefaultOptions={true}
                    style={SELECT_DARK}
                  />
                </div>
              ) : null}
            </div>

            <div className="mx-auto h-px w-2/3 bg-white/10" />

            {/* Phone verification */}
            <div>
              <h3 className={`mb-1 flex items-center gap-2 ${HEADING}`}>
                <Smartphone className="h-4 w-4" />
                Phone verification
              </h3>
              <p className="mb-3 text-xs text-neutral-300">
                We text your signing link here, so we make sure it&apos;s
                really your number.
              </p>
              <Field id="register-phone" label="Phone number" required bind={false}>
                <div className="flex gap-2">
                  <InputMask
                    id="register-phone"
                    className={`${INPUT} flex-1 disabled:opacity-60`}
                    type="tel"
                    inputMode="tel"
                    name="phone"
                    mask="999-999-9999"
                    placeholder="111-111-1111"
                    value={form.phone}
                    onChange={onChange}
                    onBlur={() => setTouched((p) => ({ ...p, phone: true }))}
                    disabled={phoneVerified}
                    required
                  />
                  {phoneVerified ? (
                    <span className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md bg-emerald-400/15 px-3 text-sm font-medium text-emerald-300">
                      <CheckCircle2 className="h-4 w-4" />
                      Verified
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSendCode}
                      disabled={!isPhoneValid || sendCodeMutation.isLoading || cooldownSeconds > 0}
                      className={`h-10 shrink-0 ${BUTTON}`}
                    >
                      {sendCodeMutation.isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : cooldownSeconds > 0 ? (
                        `Retry in ${cooldownSeconds}s`
                      ) : verificationCodeSent ? (
                        'Resend code'
                      ) : (
                        'Verify'
                      )}
                    </button>
                  )}
                </div>
              </Field>
              {showPhoneError ? (
                <p className="mb-0 mt-1 text-xs text-rose-300">{phoneError}</p>
              ) : null}

              {verificationCodeSent && !phoneVerified ? (
                <div className="mt-3 rounded-xl bg-black/20 p-4">
                  <p className="mb-2 text-xs text-neutral-300">
                    A 6-digit verification code was sent to your phone. Enter
                    it below.
                  </p>
                  <div className="flex gap-2">
                    <InputMask
                      className={`${INPUT} flex-1 text-center font-mono text-base tracking-[0.3em]`}
                      type="text"
                      mask="999-999"
                      maskChar={null}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(formatVerificationCode(e.target.value))}
                      placeholder="000-000"
                      aria-label="Verification code"
                    />
                    <button
                      type="button"
                      onClick={handleVerifyCode}
                      disabled={getVerificationCodeDigits().length !== 6 || verifyCodeMutation.isLoading}
                      className={`h-10 shrink-0 ${BUTTON}`}
                    >
                      {verifyCodeMutation.isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Confirm'
                      )}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mx-auto h-px w-2/3 bg-white/10" />

            {/* SSN */}
            <div>
              <h3 className={`mb-1 flex items-center gap-2 ${HEADING}`}>
                <Lock className="h-4 w-4" />
                Social Security number
              </h3>
              <p className="mb-3 text-xs text-neutral-300">
                <ShieldCheck className="mr-1 inline h-3.5 w-3.5" />
                Used only to verify identity and match VA records. Your
                information is protected. See our{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-neutral-200 underline underline-offset-2 hover:text-white"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <Field id="register-ssn" label="SSN" required>
                <div className="flex gap-2">
                  <InputMask
                    id="register-ssn"
                    className={`${INPUT} flex-1`}
                    name="ssn"
                    mask="999-99-9999"
                    maskChar={null}
                    placeholder="###-##-####"
                    value={form.ssn}
                    onChange={onChange}
                    onBlur={() => setTouched((p) => ({ ...p, ssn: true }))}
                    type={showSsn ? 'text' : 'password'}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowSsn((s) => !s)}
                    aria-label={showSsn ? 'Hide SSN' : 'Show SSN'}
                    title={showSsn ? 'Hide SSN' : 'Show SSN'}
                    className={`h-10 w-10 shrink-0 !px-0 ${BUTTON}`}
                  >
                    <FontAwesomeIcon icon={showSsn ? faEyeSlash : faEye} className="h-4 w-4" />
                  </button>
                </div>
              </Field>
              {showSsnError ? (
                <p className="mb-0 mt-1 text-xs text-rose-300">{ssnError}</p>
              ) : null}
            </div>

            {/* Contact consent — required (the whole onboarding flow calls,
                texts, and emails this person). */}
            <div className="rounded-lg bg-black/20 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  name="agreeToContact"
                  checked={form.agreeToContact}
                  onChange={onChange}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-400"
                />
                <span className="text-sm text-white">
                  I agree to be contacted by Veritas Ratings by phone, text,
                  and email
                </span>
              </label>
            </div>

            <div>
              {!phoneVerified ? (
                <p className="mb-2 text-center text-xs text-amber-200">
                  Verify your phone number to enable registration.
                </p>
              ) : !form.agreeToContact ? (
                // The button needs both (QA 2026-10-13): say what's missing.
                <p className="mb-2 text-center text-xs text-amber-200">
                  Check the box above to agree to be contacted.
                </p>
              ) : null}
              <button
                type="submit"
                disabled={!canSubmit}
                className={`w-full py-2.5 ${BUTTON}`}
              >
                {callApi.isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Create my account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </PublicLayout>
  );
};

export default RegisterV2;
